import { useTranslation } from '@mint-lib/i18n';
import type { ClientType } from '@mint-lib/profile-helpers';

import {
  Avatar,
  AvatarSize,
  Dialog,
  Pagination,
  SearchBar,
  SelectionListItem,
  Truncation,
} from '@myn/mui';

import { useAgencyClients } from '../../api/queries/agencyClients.js';
import { UserType } from '../../api/users/index.js';
import useClientsPaginationAndSearch from '../../hooks/useClientsPaginationAndSearch/useClientsPaginationAndSearch.jsx';

type Props = {
  className?: string;
  onClose: () => void;
  userProfile: Partial<UserType>;
  isActive: boolean;
};

const ClientsModal: React.FC<Props> = ({ onClose, isActive, userProfile }) => {
  const { t } = useTranslation('@myn/permissions');
  const { agencyClients } = useAgencyClients();

  const clients =
    userProfile.assignedClients?.map((assignedClient) => {
      return agencyClients?.find((client) => client.id === assignedClient);
    }) || [];

  const { data, search, pagination } = useClientsPaginationAndSearch(
    clients as ClientType[],
  );

  return (
    <Dialog
      open={isActive}
      onClose={onClose}
      title={t('Clients assigned to this user')}
      size="m"
      submitButtonProps={{
        label: t('Close'),
        onClick: onClose,
        color: 'primary',
      }}
    >
      <div className="flex min-h-160 flex-col justify-between gap-4">
        <div className="flex flex-col gap-4">
          <Truncation
            tooltipTitle={`${userProfile.firstName} ${userProfile.lastName}`}
            maxWidth={'648px'}
            variant="bodyShort01"
          >
            {t('These are all the clients assigned to {{fullName}}.', {
              fullName: `${userProfile.firstName} ${userProfile.lastName}`,
            })}
          </Truncation>
          {clients.length > 1 ? (
            <SearchBar
              {...search}
              size="small"
              placeholder={`${t('Search client')}`}
              sx={{
                maxWidth: '100%',
              }}
            />
          ) : null}
          <div className="flex flex-col gap-2">
            {data.map((client) => {
              return (
                <SelectionListItem
                  key={client?.id}
                  readOnly
                  text={client?.name || ''}
                  startAdornment={
                    <Avatar
                      firstName={client?.name}
                      src={client?.logo}
                      avatarSize={AvatarSize.small}
                    />
                  }
                />
              );
            })}
          </div>
        </div>
        {pagination.count > 1 && (
          <div className="flex justify-center">
            <Pagination size="medium" {...pagination} />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ClientsModal;
